import { useCallback } from 'react';
import { routes } from '~/constants';
import { useIoContext, useOverlayContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { sendErrReport } from '~/infra/utils';
import { ANDROID_SCHEME_CALLBACK_PATH, androidCallbackHost, OAUTH_PROVIDER_ROUTES } from '../../shared/oauth-config';
import { parseOAuthCallback } from './oauth-callback-listener';
import { toExplainedOAuthFailure } from './oauthLoginFailure.utils';
import { makeOAuthStateStorage } from './oauthStateStorage';
import { useShowOAuthLoginFailure } from './useShowOAuthLoginFailure';
export function useOAuthSignIn(provider) {
    const { browser: { changeLocation, getOrigin, scrubUrlFragment }, native: { isNative, androidCallbackScheme, systemBrowser }, cookies, localStorage } = useIoContext();
    const { showErrorToast } = useOverlayContext();
    const showOAuthLoginFailure = useShowOAuthLoginFailure();
    const { t: commonTexts } = useLocaleKeys('common');
    const providerErrorMessage = provider === 'qbo' ? commonTexts.toasts.failedToContinueWithIntuit() : commonTexts.toasts.failedToContinueWithXero();
    const showProviderErrorToast = useCallback(()=>{
        showErrorToast(providerErrorMessage, "native-oauth-".concat(provider, "-error-toast"));
    }, [
        provider,
        providerErrorMessage
    ]);
    const commitCallbackResult = useCallback((result)=>{
        const oauthStateStorage = makeOAuthStateStorage(localStorage);
        if (result.kind === 'ignored') {
            oauthStateStorage.clearPendingState(provider);
            sendErrReport(new Error("native-oauth-callback-ignored: ".concat(provider)));
            showProviderErrorToast();
            return 'failed';
        }
        if (result.kind === 'error') {
            oauthStateStorage.clearPendingState(result.provider);
            const explainedFailure = toExplainedOAuthFailure(result.error);
            if (explainedFailure == null) {
                sendErrReport(new Error("native-oauth-callback-error: ".concat(result.error)));
                showProviderErrorToast();
            } else {
                showOAuthLoginFailure(result.provider, explainedFailure);
            }
            return 'failed';
        }
        const expectedState = oauthStateStorage.readPendingState(result.provider);
        if (expectedState == null || expectedState !== result.state) {
            oauthStateStorage.clearPendingState(result.provider);
            sendErrReport(new Error("native-oauth-state-mismatch: ".concat(result.provider)));
            showProviderErrorToast();
            return 'failed';
        }
        try {
            cookies.setAuthTokenForNative(result.authToken);
        } catch (err) {
            oauthStateStorage.clearPendingState(result.provider);
            sendErrReport(err);
            showProviderErrorToast();
            return 'failed';
        }
        oauthStateStorage.clearPendingState(result.provider);
        scrubUrlFragment();
        changeLocation(routes.home);
        return 'signedIn';
    }, [
        provider,
        showProviderErrorToast,
        showOAuthLoginFailure
    ]);
    const signIn = useCallback(async (authUrl)=>{
        if (!isNative) {
            changeLocation(authUrl);
            return 'redirecting';
        }
        const oauthStateStorage = makeOAuthStateStorage(localStorage);
        const isAndroidCallback = androidCallbackScheme != null;
        let backendState;
        let callbackHost;
        try {
            backendState = new URL(authUrl).searchParams.get('state');
            callbackHost = isAndroidCallback ? androidCallbackHost(provider) : new URL(__BASE_URL__).host;
        } catch (err) {
            sendErrReport(err);
            showProviderErrorToast();
            return 'failed';
        }
        if (backendState == null) {
            sendErrReport(new Error("native-oauth-no-state-in-intuit-url: ".concat(provider)));
            showProviderErrorToast();
            return 'failed';
        }
        oauthStateStorage.persistPendingState(provider, backendState);
        const callbackPath = isAndroidCallback ? ANDROID_SCHEME_CALLBACK_PATH : OAUTH_PROVIDER_ROUTES[provider].webCallbackPath;
        try {
            const result = await systemBrowser.runOAuthRedirectFlow(authUrl, callbackHost, callbackPath);
            if (result.kind === 'canceled') {
                oauthStateStorage.clearPendingState(provider);
                return 'canceled';
            }
            return commitCallbackResult(parseOAuthCallback(result.url));
        } catch (err) {
            oauthStateStorage.clearPendingState(provider);
            sendErrReport(err);
            showProviderErrorToast();
            return 'failed';
        }
    }, [
        provider,
        commitCallbackResult,
        showProviderErrorToast
    ]);
    return {
        authRequestParams: {
            native: isNative,
            androidCallbackScheme: androidCallbackScheme !== null && androidCallbackScheme !== void 0 ? androidCallbackScheme : undefined
        },
        webOrigin: isNative ? __BASE_URL__ : getOrigin(),
        signIn
    };
}
